import { useEffect } from "react";

import { produce } from "immer";
import { UseFormReturn } from "react-hook-form";

import type { AnyStepFormData } from "../../Steps/AnyStepFormData";
import { useLiveFormEditorContext } from "../LiveFormEditorContext";

import { useBuildInitialLiveTicketingData } from "./useBuildInitialLiveTicketingData";
import { usePreviewFormWatch } from "./usePreviewFormWatch";

import { LiveTicketingData } from "@/features/LiveFormEditor/types/LiveFormEditor.types";
import { useCurrentUserContext } from "@/common/contexts/CurrentUserContext";
import { toTicketingPreview } from "@/common/helpers/formPreview";

type MutateLiveTicketingData<T extends AnyStepFormData> = (formData: T, draft: LiveTicketingData) => void;

/**
 * This hook keeps the "liveTicketingData" in sync with form updates.
 * It uses "immer" to create immutable updates, ensuring we always have a fresh, updated state.
 */
export const useUpdateTicketingDataInPreview = <T extends AnyStepFormData>({
  mutate: mutateLiveTicketingData,
  form,
}: {
  mutate: MutateLiveTicketingData<T>;
  form: UseFormReturn<T>;
}) => {
  const {
    setLiveFormData,
    liveFormData,
    isStepChanging,
    apiFormObject: apiTicketing,
  } = useLiveFormEditorContext<"ticketing">();
  const { organization } = useCurrentUserContext();

  const ticketingPreview = toTicketingPreview(apiTicketing, organization);

  const { buildInitialLiveTicketingData } = useBuildInitialLiveTicketingData();

  // #1: Initialize "liveTicketingData" on step mount
  useEffect(() => {
    if (isStepChanging) {
      return;
    }

    if (!ticketingPreview) {
      return;
    }

    // Build the initial "liveTicketingData" object from the API data
    const initialResult = buildInitialLiveTicketingData(ticketingPreview);

    // Update the context with the default form data (e.g: placeholder values)
    updateLiveTicketingDataWithFormData(initialResult);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStepChanging]);

  const syncPreviewWithLatestData = () => {
    if (!liveFormData || isStepChanging) {
      return;
    }

    updateLiveTicketingDataWithFormData(liveFormData as LiveTicketingData);
  };

  usePreviewFormWatch(form, syncPreviewWithLatestData);

  useEffect(() => {
    syncPreviewWithLatestData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketingPreview?.rateCategories]);

  // Helper function to update the liveTicketingData with the form data
  const updateLiveTicketingDataWithFormData = (liveTicketingDataToPreserve: LiveTicketingData) => {
    // React setState does not allow us to mutate the existing object directly.
    // Because liveTicketingData is a complex and deeply nested object (e.g: liveTicketingData.ticketing.ticketingFields[0].title),
    // we are using immer.produce to manipulate it as if it were mutable,
    // but in the end immer produces a brand-new, immutable object for us.
    const newLiveData = produce(liveTicketingDataToPreserve, (liveTicketingDataToMutate) => {
      liveTicketingDataToMutate.ticketing.rateCategories = ticketingPreview?.rateCategories;
      mutateLiveTicketingData(form.getValues(), liveTicketingDataToMutate);
    });

    setLiveFormData(newLiveData);
  };
};
